import { createClient } from '@supabase/supabase-js';
import type { Customer } from '../types/customer';
import type { Product } from '../types/product';
import type { Sale } from '../types/sale';
import type { Transaction } from '../types/financial';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
});

// Helper to get current user ID
export const getCurrentUserId = async (): Promise<string | null> => {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.user?.id || null;
};

// Helper to check if error is a foreign key violation
const isForeignKeyViolation = (error: any) => {
  return error?.code === '23503';
};

// Helper to check if error is a unique constraint violation
const isUniqueViolation = (error: any) => {
  return error?.code === '23505';
};

// Database operations with proper user isolation
export const db = {
  customers: {
    async create(data: Omit<Customer, 'id' | 'createdAt' | 'updatedAt'>) {
      const userId = await getCurrentUserId();
      if (!userId) throw new Error('User not authenticated');

      const { data: customer, error } = await supabase
        .from('customers')
        .insert({
          user_id: userId,
          name: data.name,
          type: data.type,
          document: data.document,
          email: data.email,
          phone: data.phone,
          whatsapp: data.whatsapp,
          status: data.status || 'active',
          address_street: data.address_street,
          address_number: data.address_number,
          address_complement: data.address_complement,
          address_neighborhood: data.address_neighborhood,
          address_city: data.address_city,
          address_state: data.address_state,
          address_zip: data.address_zip,
          notes: data.notes
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating customer:', error);
        
        if (isUniqueViolation(error)) {
          throw new Error('Já existe um cliente com este documento');
        }
        
        throw error;
      }

      return customer;
    },

    async update(id: string, data: Partial<Customer>) {
      const userId = await getCurrentUserId();
      if (!userId) throw new Error('User not authenticated');

      const { data: customer, error } = await supabase
        .from('customers')
        .update({
          name: data.name,
          type: data.type,
          document: data.document,
          email: data.email,
          phone: data.phone,
          whatsapp: data.whatsapp,
          status: data.status,
          address_street: data.address_street,
          address_number: data.address_number,
          address_complement: data.address_complement,
          address_neighborhood: data.address_neighborhood,
          address_city: data.address_city,
          address_state: data.address_state,
          address_zip: data.address_zip,
          notes: data.notes,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .eq('user_id', userId)
        .is('deleted_at', null)
        .select()
        .single();

      if (error) {
        console.error('Error updating customer:', error);
        
        if (isUniqueViolation(error)) {
          throw new Error('Já existe um cliente com este documento');
        }
        
        throw error;
      }

      return customer;
    },

    async list() {
      const userId = await getCurrentUserId();
      if (!userId) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('user_id', userId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error listing customers:', error);
        throw error;
      }

      return data || [];
    },

    async delete(id: string) {
      const userId = await getCurrentUserId();
      if (!userId) throw new Error('User not authenticated');

      // Soft delete - update deleted_at timestamp
      const { error } = await supabase
        .from('customers')
        .update({ 
          deleted_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .eq('user_id', userId)
        .is('deleted_at', null);

      if (error) {
        console.error('Error deleting customer:', error);
        
        if (isForeignKeyViolation(error)) {
          throw new Error('Não é possível excluir o cliente pois existem registros vinculados');
        }
        
        throw error;
      }
    }
  },

  products: {
    async create(data: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>) {
      const userId = await getCurrentUserId();
      if (!userId) throw new Error('User not authenticated');

      const { data: product, error } = await supabase
        .from('products')
        .insert({
          user_id: userId,
          name: data.name,
          code: data.code,
          barcode: data.barcode,
          description: data.description,
          category: data.category,
          subcategory: data.subcategory,
          price: data.price,
          cost: data.cost,
          stock: data.stock,
          min_stock: data.min_stock,
          status: data.status || 'active',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return product;
    },

    async update(id: string, data: Partial<Product>) {
      const userId = await getCurrentUserId();
      if (!userId) throw new Error('User not authenticated');

      const { data: product, error } = await supabase
        .from('products')
        .update({
          name: data.name,
          code: data.code,
          barcode: data.barcode,
          description: data.description,
          category: data.category,
          subcategory: data.subcategory,
          price: data.price,
          cost: data.cost,
          stock: data.stock,
          min_stock: data.min_stock,
          status: data.status,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .eq('user_id', userId)
        .select()
        .single();

      if (error) throw error;
      return product;
    },

    async list() {
      const userId = await getCurrentUserId();
      if (!userId) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    }
  },

  sales: {
    async create(data: Omit<Sale, 'id' | 'createdAt' | 'updatedAt'>) {
      const userId = await getCurrentUserId();
      if (!userId) throw new Error('User not authenticated');

      const { data: sale, error } = await supabase
        .from('sales')
        .insert([{
          ...data,
          user_id: userId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;
      return sale;
    },

    async update(id: string, data: Partial<Sale>) {
      const userId = await getCurrentUserId();
      if (!userId) throw new Error('User not authenticated');

      const { data: sale, error } = await supabase
        .from('sales')
        .update({
          ...data,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .eq('user_id', userId)
        .select()
        .single();

      if (error) throw error;
      return sale;
    },

    async list() {
      const userId = await getCurrentUserId();
      if (!userId) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('sales')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    }
  },

  transactions: {
    async create(data: Omit<Transaction, 'id' | 'createdAt' | 'updatedAt'>) {
      const userId = await getCurrentUserId();
      if (!userId) throw new Error('User not authenticated');

      const { data: transaction, error } = await supabase
        .from('transactions')
        .insert([{
          ...data,
          user_id: userId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;
      return transaction;
    },

    async update(id: string, data: Partial<Transaction>) {
      const userId = await getCurrentUserId();
      if (!userId) throw new Error('User not authenticated');

      const { data: transaction, error } = await supabase
        .from('transactions')
        .update({
          ...data,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .eq('user_id', userId)
        .select()
        .single();

      if (error) throw error;
      return transaction;
    },

    async list(type?: 'income' | 'expense') {
      const userId = await getCurrentUserId();
      if (!userId) throw new Error('User not authenticated');

      let query = supabase
        .from('transactions')
        .select('*')
        .eq('user_id', userId);

      if (type) {
        query = query.eq('type', type);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    }
  }
};